import {UserPen, UserPlus} from 'lucide-react';
import {useIntl} from 'react-intl';
import type {Call, DetectedContactDetails} from '../../../../store/features/calls/callsService';
import {useGetContactByIdQuery} from '../../../../store/features/Contacts/contactsService';
import {insightsMessages as m} from '../insightsMessages';
import {lucide, sectionTitleBase, tintButton} from '../insightsUi';
import {Citations} from './CitationChip';
import {hasAnyField, missingFields, suggestionPrefill, type ContactPrefill} from './contactSuggestion';

export interface ContactPanelRequest {
    mode: 'create' | 'complete';
    prefill: ContactPrefill;
    contactId?: number;
}

interface ContactSuggestionCardProps {
    call: Call;
    details: DetectedContactDetails;
    onOpen: (request: ContactPanelRequest) => void;
}

const Fields = ({prefill}: {prefill: ContactPrefill}) => {
    const intl = useIntl();
    const name = [prefill.firstName, prefill.lastName].filter(Boolean).join(' ');
    const rows: [string, string | undefined][] = [
        [intl.formatMessage(m.contactName), name || undefined],
        [intl.formatMessage(m.contactCompany), prefill.company],
        [intl.formatMessage(m.contactEmail), prefill.email],
    ];
    return (
        <dl className="m-0 grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-1.5">
            {rows.filter(([, value]) => value).map(([label, value]) => (
                <div key={label} className="contents">
                    <dt className="text-footnote text-ink-secondary">{label}</dt>
                    <dd className="m-0 truncate text-callout text-ink">{value}</dd>
                </div>
            ))}
        </dl>
    );
};

export const ContactSuggestionCard = ({call, details, onOpen}: ContactSuggestionCardProps) => {
    const intl = useIntl();
    const contact = call.internalCallee ? null : call.primaryContact ?? null;
    const isPipedrive = contact?.contactType?.source === 'PIPEDRIVE';
    const contactId = contact?.contactId;
    const {data: existing, isFetching} = useGetContactByIdQuery(contactId as number, {skip: !contactId || isPipedrive});

    if (call.internalCallee || isPipedrive) return null;

    const known = Boolean(contactId);
    const prefill = known ? missingFields(existing ?? null, details) : suggestionPrefill(details);
    if ((known && (isFetching || !existing)) || !hasAnyField(prefill)) return null;
    if (!known && !call.clientMsisdn) return null;

    return (
        <section aria-labelledby="insights-contact-title" className="flex flex-col gap-3 rounded-card bg-surface-muted p-4" data-testid="insights-contact-suggestion">
            <div className="flex flex-col gap-0.5">
                <h3 id="insights-contact-title" className={`${sectionTitleBase} text-ink-muted-strong`}>{intl.formatMessage(m.contactTitle)}</h3>
                <span className="text-footnote text-ink-muted-strong">
                    {intl.formatMessage(m.contactHint)}
                    <Citations citations={details.citations}/>
                </span>
            </div>
            <Fields prefill={prefill}/>
            <div>
                <button
                    type="button"
                    onClick={() => onOpen(known ? {mode: 'complete', prefill, contactId} : {mode: 'create', prefill})}
                    className={tintButton}
                >
                    {known ? <UserPen size={14} aria-hidden="true" className={lucide}/> : <UserPlus size={14} aria-hidden="true" className={lucide}/>}
                    {intl.formatMessage(known ? m.contactComplete : m.contactSave)}
                </button>
            </div>
        </section>
    );
};
